import { Controller } from "@hotwired/stimulus"
import {
  withCheckoutCompletedParameter,
  withoutCheckoutCompletedParameter,
  withoutCheckoutParameter
} from "../lib/checkout_url"

export default class extends Controller {
  static targets = ["status"]
  static values = {
    token: String,
    environment: String,
    email: String,
    autoOpen: Boolean,
    autoOpenPriceId: String,
    autoOpenCredits: Number,
    autoOpenClaim: String,
    messages: { type: Object, default: {} }
  }

  async connect() {
    this.connected = true
    this.removeCompletionMarker()

    try {
      await this.loadPaddle()
    } catch (_error) {
      this.showStatus(this.message("unavailable"))
      return
    }
    if (!this.connected) return

    const eventCallback = (event) => this.handleEvent(event)
    if (window.dropkindPaddleInitialized) {
      window.Paddle.Update({ eventCallback })
      this.openAutomatically()
      return
    }

    if (this.environmentValue === "sandbox") window.Paddle.Environment.set("sandbox")
    window.Paddle.Initialize({ token: this.tokenValue, eventCallback })
    window.dropkindPaddleInitialized = true
    this.openAutomatically()
  }

  disconnect() {
    this.connected = false
  }

  open(event) {
    if (!window.Paddle || !window.dropkindPaddleInitialized) return

    this.track("paid_pdf_checkout_opened")
    this.completedCredits = Number(event.params.credits)

    const checkout = {
      settings: { displayMode: "overlay", variant: "one-page", theme: this.overlayTheme() },
      items: [{ priceId: event.params.priceId, quantity: 1 }],
      customData: {
        service: "dropkind",
        checkout_claim: event.params.claim
      }
    }
    if (this.hasEmailValue && this.emailValue) checkout.customer = { email: this.emailValue }

    window.Paddle.Checkout.open(checkout)
  }

  // The rest of the app follows prefers-color-scheme, so a hardcoded light overlay was the one
  // surface that flashed white at a dark-mode reader, and it is the one asking for card details.
  overlayTheme() {
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  }

  openAutomatically() {
    if (!this.autoOpenValue || !this.hasAutoOpenPriceIdValue || !this.hasAutoOpenClaimValue) return
    this.completionUrl = withoutCheckoutParameter(window.location.href)
    window.history.replaceState(window.history.state, "", this.completionUrl)
    this.open({ params: { priceId: this.autoOpenPriceIdValue, credits: this.autoOpenCreditsValue, claim: this.autoOpenClaimValue } })
  }

  handleEvent(event) {
    if (event.name !== "checkout.completed") return

    this.showStatus(this.message("completed"))
    this.track("paid_pdf_checkout_completed")
    const destination = withCheckoutCompletedParameter(
      this.completionUrl || withoutCheckoutParameter(window.location.href),
      this.completedCredits
    )
    window.setTimeout(() => window.Turbo?.visit(destination, { action: "replace" }), 2500)
  }

  removeCompletionMarker() {
    const url = new URL(window.location.href)
    if (url.searchParams.get("payment") !== "completed") return

    window.history.replaceState(window.history.state, "", withoutCheckoutCompletedParameter(window.location.href))
  }

  showStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  message(key) {
    return this.messagesValue[key] || ""
  }

  loadPaddle() {
    if (window.Paddle) return Promise.resolve()
    if (window.dropkindPaddleLoading) return window.dropkindPaddleLoading

    window.dropkindPaddleLoading = new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = "https://cdn.paddle.com/paddle/v2/paddle.js"
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
    return window.dropkindPaddleLoading
  }

  track(event) {
    if (typeof window.gtag === "function") window.gtag("event", event)
  }
}
